import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionPermissions } from '../../@types/permission.type';
import { ActivityMatrixGroup, ActivityMatrixRow } from '../../@types/service-activity.type';
import { isUserHasPermission } from '../../configs/permissions';
import { useGetServiceActivityMonitorData } from '../../queries/service-activity-monitor-query';
import { useGetUserSystemPermissions } from '../../queries/user-query';
import { getServiceActivityMonitorTime } from '../../utils/date-time-utils';
import { buildFlowRail } from '../../utils/service-activity-flow';
import { buildActivityMatrix } from '../../utils/service-activity-matrix';
import { buildServicesFilterURL, OPEN_STATUS_NAMES } from '../../utils/service-activity-url';

/**
 * Query, refresh control and the four drill-through navigations for the home
 * page.
 *
 * Row and group drill-downs carry `OPEN_STATUS_NAMES` explicitly: a URL with
 * `clientCode` sets `hasChipState`, which suppresses the job list's default
 * open-status filter, so without them the destination would include Complete
 * and Cancelled jobs and its count would not match the number just clicked.
 */
export const useActivityMonitor = () => {
  const navigate = useNavigate();
  const [refreshEnabled, setRefreshEnabled] = useState(true);

  const query = useGetServiceActivityMonitorData(refreshEnabled);
  const permissionsQuery = useGetUserSystemPermissions();

  // Home itself is not permission-gated, but every drill-through targets
  // `/services`, which is. Without the permission the counts render as static
  // text rather than links into a route the user cannot open.
  //
  // While the permissions query is still in flight its data is undefined, which
  // would read as "denied" — the matrix would render inert and then flip to
  // interactive. The page holds its skeleton until permissions resolve (see
  // `isLoading` below) so that flicker never reaches the user.
  const canDrillDown = isUserHasPermission(ActionPermissions.Service_Search, permissionsQuery.data);

  // React Query's default `structuralSharing` keeps `data` referentially stable
  // when a poll returns an identical payload, so these memos genuinely skip
  // work every 120s rather than recomputing on a new-but-equal array.
  const matrix = useMemo(() => buildActivityMatrix(query.data), [query.data]);
  const lanes = useMemo(() => buildFlowRail(query.data), [query.data]);

  const lastUpdatedTime = useMemo(
    () => (query.dataUpdatedAt ? getServiceActivityMonitorTime(new Date(query.dataUpdatedAt)) : ''),
    [query.dataUpdatedAt]
  );

  // Depend on `refetch`, not the whole query object — the latter changes
  // identity every render and would rebuild this callback each time.
  const { refetch } = query;
  const handleRefreshToggle = useCallback(() => {
    // The refetch stays outside the state updater. React may invoke an updater
    // more than once (StrictMode, concurrent rendering), so a side effect in
    // there can fire a duplicate request; updaters must stay pure.
    const resuming = !refreshEnabled;
    setRefreshEnabled(resuming);
    if (resuming) refetch();
  }, [refreshEnabled, refetch]);

  const navigateTo = useCallback(
    (target: Parameters<typeof buildServicesFilterURL>[0]) => {
      if (!canDrillDown) return;
      navigate(buildServicesFilterURL(target));
    },
    [navigate, canDrillDown]
  );

  const handleStageSelect = useCallback(
    (serviceStatusGroup: string) => navigateTo({ serviceStatusGroup }),
    [navigateTo]
  );

  const handleCellSelect = useCallback(
    (row: ActivityMatrixRow, serviceStatusGroup: string) =>
      navigateTo({
        clientCode: row.clientCode,
        serviceTypeName: row.serviceTypeName,
        serviceStatusGroup,
      }),
    [navigateTo]
  );

  const handleRowSelect = useCallback(
    (row: ActivityMatrixRow) =>
      navigateTo({
        clientCode: row.clientCode,
        serviceTypeName: row.serviceTypeName,
        serviceStatusNames: OPEN_STATUS_NAMES,
      }),
    [navigateTo]
  );

  const handleGroupSelect = useCallback(
    (group: ActivityMatrixGroup) =>
      navigateTo({ clientCode: group.clientCode, serviceStatusNames: OPEN_STATUS_NAMES }),
    [navigateTo]
  );

  return {
    // Both queries must settle before first paint — see canDrillDown above.
    isLoading: query.isLoading || permissionsQuery.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    matrix,
    lanes,
    lastUpdatedTime,
    refreshEnabled,
    handleRefreshToggle,
    canDrillDown,
    handleStageSelect,
    handleCellSelect,
    handleRowSelect,
    handleGroupSelect,
  };
};
